import { AppModal, Button } from "@/components/ui";
import type { MatterDashboardTab } from "@/lib/types/MatterDashboardTab";
import { ArrowDown, ArrowUp } from "lucide-react";
import { useState } from "react";

export function DashboardTabReorderModal({
  tabs,
  saving,
  error,
  onClose,
  onSave,
}: {
  tabs: MatterDashboardTab[];
  saving: boolean;
  error: string;
  onClose: () => void;
  onSave: (tabs: MatterDashboardTab[]) => void;
}) {
  const [draft, setDraft] = useState(tabs);
  const changed = draft.some((tab, index) => tab.id !== tabs[index]?.id);

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= draft.length) return;
    setDraft((current) => {
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  return (
    <AppModal
      open
      onOpenChange={(open) => {
        if (!open && !saving) onClose();
      }}
      title="Reorder tabs"
      size="md"
      isDirty={changed}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onSave(draft);
            }}
            disabled={!changed || saving}
          >
            Done
          </Button>
        </div>
      }
    >
      <div className="divide-border divide-y rounded-md border">
        {draft.map((tab, index) => (
          <div
            key={tab.id}
            className="flex min-h-12 items-center justify-between gap-3 px-3 py-2"
          >
            <span className="min-w-0 truncate text-sm">{tab.title}</span>
            <div className="flex shrink-0 gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                aria-label={`Move ${tab.title} up`}
                disabled={index === 0 || saving}
                onClick={() => {
                  move(index, -1);
                }}
              >
                <ArrowUp />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                aria-label={`Move ${tab.title} down`}
                disabled={index === draft.length - 1 || saving}
                onClick={() => {
                  move(index, 1);
                }}
              >
                <ArrowDown />
              </Button>
            </div>
          </div>
        ))}
      </div>
      {error && (
        <p className="text-destructive mt-2 text-sm" role="alert">
          {error}
        </p>
      )}
    </AppModal>
  );
}
